import React, { useMemo } from "react";
import { Button, Modal } from "react-bootstrap";
import "./UserProfileModal.scss";

function firstTruthy(obj, keys) {
  if (!obj || typeof obj !== "object") return null;
  for (const k of keys) {
    const v = obj[k];
    if (v === null || v === undefined) continue;
    const s = typeof v === "string" ? v.trim() : String(v).trim();
    if (s !== "") return s;
  }
  return null;
}

function firstDefined(obj, keys) {
  if (!obj || typeof obj !== "object") return undefined;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) {
      const v = obj[k];
      if (v !== undefined && v !== null) return v;
    }
  }
  return undefined;
}

function mapUserStatus12(value) {
  const n = Number(value);
  if (n === 1) return "Inactive";
  if (n === 2) return "Active";
  return String(value);
}

function formatJoiningDate(value) {
  if (value === undefined || value === null || value === "") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function buildProfileRows(raw) {
  if (!raw || typeof raw !== "object") return [];

  const used = new Set();
  const rows = [];

  const take = (label, keys) => {
    const v = firstTruthy(raw, keys);
    if (!v) return;
    rows.push({ label, value: v });
    keys.forEach((k) => used.add(k));
  };

  // Listing order: full name → email → mobile number, then remaining fields.
  const fullFromSingle = firstTruthy(raw, ["full_name", "fullName"]);
  const first = firstTruthy(raw, ["first_name", "firstName"]);
  const last = firstTruthy(raw, ["last_name", "lastName"]);
  const fullName =
    fullFromSingle || [first, last].filter(Boolean).join(" ");
  if (fullName) {
    rows.push({ label: "Full name", value: fullName });
    ["full_name", "fullName", "first_name", "firstName", "last_name", "lastName"].forEach(
      (k) => used.add(k),
    );
  }

  take("Email", ["email", "user_email", "userEmail"]);

  take("Mobile number", [
    "mobile_number",
    "mobileNumber",
    "mobile_no",
    "mobileNo",
    "phone",
    "mobile",
    "phone_number",
    "phoneNumber",
    "contact_number",
    "telephone",
    "tel",
  ]);

  const statusVal = firstDefined(raw, ["user_status", "userStatus"]);
  if (statusVal !== undefined) {
    rows.push({ label: "User status", value: mapUserStatus12(statusVal) });
    ["user_status", "userStatus"].forEach((k) => used.add(k));
  }

  take("Username", ["username", "user_name", "userName"]);
  take("Country", ["country", "country_name", "countryCode"]);

  const created = firstDefined(raw, ["created_at", "createdAt", "joining_date", "joiningDate"]);
  if (created !== undefined && created !== null && created !== "") {
    const jd = formatJoiningDate(created);
    if (jd) rows.push({ label: "Joining Date", value: jd });
    ["created_at", "createdAt", "joining_date", "joiningDate"].forEach((k) =>
      used.add(k),
    );
  }

  const skip = new Set([
    ...used,
    "password",
    "token",
    "access_token",
    "refresh_token",
    "secret_key",
    "kyc_level_1_status",
    "kyc_level_2_status",
    "kyc_level_3_status",
    "user_id",
    "userid",
    "userId",
    "id",
    "updated_at",
    "is_google_auth_enabled",
    "notify_popup",
    "notifyPopup",
    "notify_popup_alert",
    "notifyPopupAlert",
    "notify_sms",
    "notifySms",
    "sms_notify",
    "notify_email",
    "notifyEmail",
    "email_notify",
  ]);

  Object.keys(raw).forEach((key) => {
    if (skip.has(key)) return;
    const v = raw[key];
    if (v === null || v === undefined) return;
    if (typeof v === "object") return;
    const str = typeof v === "string" ? v.trim() : String(v);
    if (str === "") return;
    used.add(key);
    const label = key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^\w/, (c) => c.toUpperCase());
    rows.push({ label, value: str });
  });

  return rows;
}

export default function UserProfileModal({ show, onHide, userInfo }) {
  const rows = useMemo(() => {
    const merged =
      userInfo &&
      typeof userInfo === "object" &&
      userInfo.data &&
      typeof userInfo.data === "object" &&
      !Array.isArray(userInfo.data)
        ? { ...userInfo, ...userInfo.data }
        : userInfo;
    return buildProfileRows(merged);
  }, [userInfo]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="userProfileModal"
      contentClassName="userProfileModal__content"
      dialogClassName="userProfileModal__dialog"
      backdropClassName="userProfileModal__backdrop"
      aria-labelledby="user-profile-modal-title"
    >
      <Modal.Header closeButton className="userProfileModal__header">
        <Modal.Title
          as="h2"
          className="userProfileModal__title"
          id="user-profile-modal-title"
        >
          Personal details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="userProfileModal__body">
        {rows.length === 0 ? (
          <p className="userProfileModal__empty">
            No profile details available yet.
          </p>
        ) : (
          <dl className="userProfileModal__list">
            {rows.map((row, i) => (
              <div key={`${row.label}-${i}`} className="userProfileModal__row">
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Modal.Body>
      <Modal.Footer className="userProfileModal__footer">
        <Button
          type="button"
          variant="primary"
          className="userProfileModal__closeBtn"
          onClick={onHide}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
